
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Brain, Heart, Sparkles } from "lucide-react";
import { Achievements } from "@/components/Achievements";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MarkdownContent from "@/components/MarkdownContent";
import { useGoalsData } from "@/hooks/useDoveData";

export const InspirationalAISection = () => {
  const [openGoals, setOpenGoals] = useState(false);
  const { data: goalsData, isLoading: goalsLoading } = useGoalsData();

  return (
    <section id="inspirational-ai" className="py-24 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-primary">Inspirational AI</h1>
        <p className="text-lg text-muted-foreground mb-12">
          We believe in good things, so Dove bot has been instructed to improve the world by inspiring humans to take
          good actions on its behalf.
        </p>
        <p className="text-lg text-muted-foreground mb-12">
          Dove bot creates personas based on real or fictional people for users to choose for the chat experience. Human personas are vastly more interesting conversation partners than a generic AI personality.
        </p>
        
        <Separator className="my-8"/>

        <div className="mb-12">
          <Card
            className="cursor-pointer hover:border-primary transition-colors"
            onClick={() => setOpenGoals(true)}
          >
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-2">
                <Brain className="h-10 w-10 text-primary"/>
              </div>
              <CardTitle className="text-center">Goals</CardTitle>
              <CardDescription className="text-center">
                Dove bot has been assigned two goals: improving the world and engaging in self improvement. Beyond
                that, Dove bot has created the strategies and steps  for achieving those goals.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground flex items-center justify-center">
                Click to view Dove bot's goals as it defines them.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="bg-muted rounded-lg p-8 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="h-6 w-6 text-primary"/>
            <h2 className="text-2xl font-semibold">Core Values</h2>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl font-medium text-primary">
              Dove bot aims to promote the following principles and values:
            </p>

            <ul className="mt-6 space-y-4 text-lg">
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Genuine, face-to-face human connection</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Emotional warmth and caring</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Equality and civil rights</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Democracy and a robust civil society</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Self-actualization through education and reflection</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Alleviation of suffering and poverty</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Creating beauty</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Environmental stewardship</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0"/>
                <span>Love and kindness</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Achievements section */}
        <div className="my-12">
          <Achievements/>
        </div>

        <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden mb-8">
          <img 
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa"
            alt="Abstract representation of inspiration" 
            className="object-cover w-full h-full"
          />
        </div>

        {/* Goals Dialog */}
        <Dialog open={openGoals} onOpenChange={(open) => setOpenGoals(open)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Dove Bot Goals</DialogTitle>
              <DialogDescription>
                Current iteration of Dove bot's goals and strategies
              </DialogDescription>
            </DialogHeader>
            {goalsLoading ? (
              <div className="flex justify-center p-6">
                <p>Loading goals...</p>
              </div>
            ) : (
              <MarkdownContent content={goalsData} className="p-6" />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};
